/**
 * useCoachAffiliations - React hook for managing coach club affiliations
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { coachAffiliationsService, CoachClubAffiliation } from '@/services/domains/coaching/coach-affiliations.service';
import { useAuth } from './useAuth';

export function useCoachAffiliations() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all coach affiliations
  const {
    data: affiliations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['coachAffiliations', profile?.id],
    queryFn: async () => {
      const response = await coachAffiliationsService.getCoachAffiliations();
      if (!response.success || !response.data) return [];
      return response.data;
    },
    enabled: !!profile?.id,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  // Get active affiliations only
  const activeAffiliations = affiliations.filter(aff => aff.status === 'active');
  const pendingAffiliations = affiliations.filter(aff => aff.status === 'pending');

  // Request to join a club
  const requestToJoinMutation = useMutation({
    mutationFn: async (clubId: string) => {
      const allProfiles = await queryClient.getQueryData<any>(['coachProfile']);
      if (!allProfiles?.id) throw new Error('No coach profile found');

      const response = await coachAffiliationsService.requestToJoinClub(clubId, allProfiles.id);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coachAffiliations'] });
    },
  });

  // Accept an affiliation invitation
  const acceptAffiliationMutation = useMutation({
    mutationFn: async (affiliationId: string) => {
      const response = await coachAffiliationsService.acceptAffiliation(affiliationId);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coachAffiliations'] });
    },
  });

  // Leave a club affiliation
  const leaveClubMutation = useMutation({
    mutationFn: async (affiliationId: string) => {
      const response = await coachAffiliationsService.leaveClub(affiliationId);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coachAffiliations'] });
    },
  });


  return {
    affiliations,
    activeAffiliations,
    pendingAffiliations,
    isLoading,
    error: error instanceof Error ? error.message : undefined,
    refetch,
    requestToJoin: requestToJoinMutation.mutate,
    isRequestingToJoin: requestToJoinMutation.isPending,
    acceptAffiliation: acceptAffiliationMutation.mutate,
    isAccepting: acceptAffiliationMutation.isPending,
    leaveClub: leaveClubMutation.mutate,
    isLeaving: leaveClubMutation.isPending,
  };
}
